import { z } from "zod";
export const SOURCE_DOCUMENT_INTAKE_LIMITS = {
    maxClaims: 50_000,
    chunkSize: 500,
    maxCanonicalBytes: 128 * 1024 * 1024,
    maxRequestBytes: 2 * 1024 * 1024,
};
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const opaque = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,159}$/);
const exact = z
    .string()
    .min(1)
    .max(160)
    .refine((v) => v.trim().length > 0, "Must contain a non-whitespace identifier");
const date = z.string().date();
const purchaseUf = z.enum([
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO",
    "UNKNOWN",
]);
export const sourceIdentifierNamespaceSchema = z.enum([
    "INTERNAL_PRODUCT_CODE",
    "SUPPLIER_SKU",
    "GTIN",
]);
export const sourceClaimCellSchema = z
    .object({
    cell: z
        .string()
        .regex(/^[A-Z]{1,3}[1-9]\d{0,6}$/)
        .optional(),
    lineStart: z.number().int().min(1).max(1_000_000).optional(),
    lineEnd: z.number().int().min(1).max(1_000_000).optional(),
    rawText: z.string().max(1000).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.cell && !v.lineStart)
        ctx.addIssue({ code: "custom", message: "A cell or line location is required" });
    if (v.lineEnd !== undefined && (!v.lineStart || v.lineEnd < v.lineStart))
        ctx.addIssue({ code: "custom", path: ["lineEnd"], message: "Invalid line range" });
});
const field = z.enum([
    "itemId",
    "category",
    "purchaseOriginUf",
    "purchaseDestinationUf",
    "paymentMethod",
    "transactionType",
    "sku",
    "description",
    "ncmRaw",
    "ncm8",
    "ncmExtension",
    "cestRaw",
    "cest",
    "unitPrice",
    "currency",
    "goodsOrigin",
    "brand",
    "warehouse",
    "expiry",
    "unit",
    "quantity",
    "commercialUnit",
    "monetaryBasis",
    "priceBasis",
    "supplierType",
    "priorSt",
    "pisCofinsRegime",
    "salePrice",
    "destinationUf",
    "buyerType",
]);
export const sourceDocumentClaimSchema = z
    .object({
    rowId: opaque,
    sourceRowSha256: hash,
    sheet: z.string().min(1).max(200).optional(),
    row: z.number().int().min(1).max(1_048_576),
    identifierNamespace: sourceIdentifierNamespaceSchema,
    itemId: exact.optional(),
    category: z.string().min(1).max(200).optional(),
    purchaseOriginUf: purchaseUf.optional(),
    purchaseDestinationUf: purchaseUf.optional(),
    paymentMethod: exact.optional(),
    transactionType: exact.optional(),
    sku: exact.optional(),
    quantity: z
        .string()
        .regex(/^-?\d+(?:\.\d{1,18})?$/)
        .max(100)
        .optional(),
    commercialUnit: z.string().max(60).optional(),
    monetaryBasis: z.enum(["UNIT", "LINE_TOTAL"]).optional(),
    priceBasis: z.enum(["GOODS_BEFORE_IPI_ST", "ACQUISITION_INCLUDES_TAXES", "UNKNOWN"]).optional(),
    supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR", "UNKNOWN"]).optional(),
    priorSt: z
        .enum(["PAID", "RETAINED", "UNKNOWN", "NEW", "PRIOR_ST_INCLUDED", "INVOICE_RETAINED"])
        .optional(),
    pisCofinsRegime: z.enum(["CUMULATIVE", "NON_CUMULATIVE", "UNKNOWN"]).optional(),
    salePrice: z
        .string()
        .regex(/^-?\d+(?:\.\d{1,18})?$/)
        .max(100)
        .optional(),
    destinationUf: z
        .enum([
        "AC",
        "AL",
        "AP",
        "AM",
        "BA",
        "CE",
        "DF",
        "ES",
        "GO",
        "MA",
        "MT",
        "MS",
        "MG",
        "PA",
        "PB",
        "PR",
        "PE",
        "PI",
        "RJ",
        "RN",
        "RS",
        "RO",
        "RR",
        "SC",
        "SP",
        "SE",
        "TO",
    ])
        .optional(),
    buyerType: z.enum(["ICMS_TAXPAYER", "NON_TAXPAYER", "FINAL_CONSUMER", "UNKNOWN"]).optional(),
    rawFields: z
        .record(z.string().min(1).max(80), z.string().max(1000))
        .refine((v) => Object.keys(v).length <= 32, "At most 32 source commercial fields are allowed")
        .optional(),
    description: z.string().min(1).max(1000).optional(),
    ncmRaw: z.string().max(160).optional(),
    ncm8: z
        .string()
        .regex(/^\d{8}$/)
        .optional(),
    ncmExtension: z.string().max(160).optional(),
    cestRaw: z.string().max(160).optional(),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .optional(),
    unitPrice: z
        .string()
        .regex(/^-?\d+(?:\.\d{1,18})?$/)
        .max(100)
        .optional(),
    currency: z.literal("BRL").optional(),
    goodsOrigin: z.number().int().min(0).max(8).optional(),
    brand: z.string().max(200).optional(),
    warehouse: z.string().max(160).optional(),
    expiry: date.optional(),
    unit: z.string().max(60).optional(),
    cells: z.partialRecord(field, sourceClaimCellSchema),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.sku && !v.description)
        ctx.addIssue({
            code: "custom",
            message: "A reviewed product identifier or description is required",
        });
    if (v.identifierNamespace === "GTIN" &&
        v.sku &&
        !/^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(v.sku))
        ctx.addIssue({
            code: "custom",
            path: ["sku"],
            message: "GTIN must have its exact supported digit length",
        });
    for (const [key, value] of Object.entries(v.cells)) {
        if (!Object.hasOwn(v, key))
            ctx.addIssue({
                code: "custom",
                path: ["cells", key],
                message: "Provenance must identify a declared field",
            });
        if (value.cell && Number(value.cell.replace(/^[A-Z]+/, "")) !== v.row)
            ctx.addIssue({
                code: "custom",
                path: ["cells", key, "cell"],
                message: "Cell must identify the exact source row",
            });
    }
    for (const key of Object.keys(v)) {
        if ([
            "rowId",
            "sourceRowSha256",
            "sheet",
            "row",
            "identifierNamespace",
            "cells",
            "rawFields",
        ].includes(key))
            continue;
        if (!v.cells[key])
            ctx.addIssue({
                code: "custom",
                path: ["cells", key],
                message: "Declared fields require source provenance",
            });
    }
});
export const sourceDocumentMetadataSchema = z
    .object({
    documentId: opaque,
    revisionId: opaque,
    runId: opaque,
    reviewId: opaque,
    sourceSha256: hash,
    normalizedSha256: hash,
    reviewSha256: hash,
    mediaType: z.enum([
        "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
    ]),
    sourceIntent: z.enum(["COMMERCIAL_REFERENCE", "SUPPLIER_DECLARATION"]),
    documentType: z.enum(["PRICE_LIST", "PRODUCT_SPECIFICATION", "OTHER"]),
})
    .strict();
export const sourceDocumentManifestSchema = z
    .object({
    claimCount: z.number().int().min(1).max(SOURCE_DOCUMENT_INTAKE_LIMITS.maxClaims),
    canonicalBytes: z.number().int().min(1).max(SOURCE_DOCUMENT_INTAKE_LIMITS.maxCanonicalBytes),
    chunkCount: z.number().int().min(1).max(100),
    canonicalization: z.literal("SOURCE_CLAIMS_JSONL_V1"),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.chunkCount !== Math.ceil(v.claimCount / SOURCE_DOCUMENT_INTAKE_LIMITS.chunkSize))
        ctx.addIssue({
            code: "custom",
            path: ["chunkCount"],
            message: "Chunk count must cover the complete deterministic manifest",
        });
});
export const sourceExtractionReviewSchema = z
    .object({
    decision: z.literal("ACCEPTED_EXTRACTION"),
    reviewedAt: z.string().datetime(),
    reviewerReference: opaque,
    acceptedClaimCount: z.number().int().min(1).max(50_000),
    acceptedNormalizedSha256: hash,
})
    .strict();
export const sourceClassificationReviewReferenceSchema = z
    .object({ reviewId: opaque, sha256: hash })
    .strict();
export const sourceCalculationReferenceSchema = z
    .object({ calculationId: z.string().uuid(), requestSha256: hash, resultSha256: hash })
    .strict();
export const sourceSupplierDeclarationSchema = z
    .object({
    supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR"]),
    evidenceRevisionId: opaque,
    evidenceSourceSha256: hash,
    validFrom: date,
    validTo: date.nullable(),
    reason: z.string().trim().min(10).max(1000),
    declaredAt: z.string().datetime(),
    actorReference: opaque,
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.validTo && v.validTo < v.validFrom)
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "End date precedes start date" });
});
export const sourceDocumentIntakeCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    bindingId: z.string().uuid(),
    workflowId: opaque,
    document: sourceDocumentMetadataSchema,
    manifest: sourceDocumentManifestSchema,
    review: sourceExtractionReviewSchema,
    classificationReview: sourceClassificationReviewReferenceSchema.optional(),
    calculation: sourceCalculationReferenceSchema.optional(),
    supplierDeclaration: sourceSupplierDeclarationSchema.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.review.acceptedClaimCount !== v.manifest.claimCount ||
        v.review.acceptedNormalizedSha256 !== v.document.normalizedSha256)
        ctx.addIssue({
            code: "custom",
            path: ["review"],
            message: "Review must accept the entire exact manifest",
        });
    if (v.supplierDeclaration &&
        (v.document.sourceIntent !== "SUPPLIER_DECLARATION" ||
            v.supplierDeclaration.evidenceRevisionId !== v.document.revisionId ||
            v.supplierDeclaration.evidenceSourceSha256 !== v.document.sourceSha256))
        ctx.addIssue({
            code: "custom",
            path: ["supplierDeclaration"],
            message: "Supplier declaration must cite this explicit supplier-declaration source revision",
        });
});
export const sourceDocumentIntakeScopeSchema = z
    .object({ bindingId: z.string().uuid(), workflowId: opaque })
    .strict();
export const sourceDocumentIntakeChunkSchema = sourceDocumentIntakeScopeSchema
    .extend({
    offset: z.number().int().min(0).max(49_999),
    claims: z.array(sourceDocumentClaimSchema).min(1).max(500),
})
    .strict()
    .superRefine((v, ctx) => {
    const ids = new Set();
    for (const [i, claim] of v.claims.entries()) {
        if (ids.has(claim.rowId))
            ctx.addIssue({
                code: "custom",
                path: ["claims", i, "rowId"],
                message: "Source row identities must be distinct; duplicate products remain separate rows",
            });
        ids.add(claim.rowId);
    }
});
export const sourceDocumentIntakeChunkReceiptSchema = z
    .object({
    intakeId: z.string().uuid(),
    chunkIndex: z.number().int().min(0).max(99),
    offset: z.number().int().nonnegative(),
    claimCount: z.number().int().min(1).max(500),
    chunkSha256: hash,
    receivedAt: z.string().datetime(),
})
    .strict();
export const sourceProcessingBlockReasonSchema = z.enum([
    "EXPLICIT_SOURCE_CATALOG_BINDING_REQUIRED",
    "EXACT_SUPPLIER_SKU_LINK_REQUIRED",
    "SUPPLIER_SKU_NAMESPACE_UNCONFIRMED",
    "CLASSIFICATION_REVIEW_REQUIRED",
    "CLASSIFICATION_REFERENCE_NOT_VERIFIED",
    "CLASSIFICATION_MISSING",
    "CALCULATION_DATE_REQUIRED",
    "PURCHASE_PRICE_REQUIRED",
    "QUANTITY_REQUIRED",
    "COMMERCIAL_UNIT_REQUIRED",
    "MONETARY_BASIS_REQUIRED",
    "PRICE_BASIS_REQUIRED",
    "SUPPLIER_PROFILE_REQUIRED",
    "PRIOR_ST_BASIS_REQUIRED",
    "PARTY_REGIME_REQUIRED",
    "DESTINATION_UF_REQUIRED",
    "BUYER_PROFILE_REQUIRED",
    "SALE_PRICE_REQUIRED",
    "CALCULATION_NOT_REQUESTED",
    "CALCULATION_REFERENCE_NOT_VERIFIED",
    "CALCULATION_SOURCE_LINK_REQUIRED",
]);
export const sourceCalculationVerificationSchema = z
    .object({
    status: z.enum([
        "NOT_REQUESTED",
        "NOT_FOUND_OR_NOT_AUTHORIZED",
        "DIGEST_MISMATCH",
        "DIGESTS_VERIFIED_SOURCE_LINK_MISSING",
    ]),
    sourceLinked: z.literal(false),
})
    .strict();
export const sourceDocumentProcessingReadinessSchema = z
    .object({
    status: z.literal("BLOCKED"),
    reasons: z
        .array(z
        .object({
        code: sourceProcessingBlockReasonSchema,
        affectedClaims: z.number().int().min(1).max(50_000),
    })
        .strict())
        .min(1)
        .max(24),
    calculationRecordVerified: z.boolean(),
    sourceToCalculationVerified: z.literal(false),
    backofficeLotSaved: z.literal(false),
})
    .strict();
export const sourceDocumentIntakeReceiptSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    receiptId: z.string().uuid(),
    intakeId: z.string().uuid(),
    bindingId: z.string().uuid(),
    organizationId: z.string().uuid(),
    sellerId: opaque,
    supplierId: z.string().uuid(),
    environment: z.literal("dev"),
    workflowId: opaque,
    document: sourceDocumentMetadataSchema,
    manifest: sourceDocumentManifestSchema,
    inputSha256: hash,
    normalizedSha256: hash,
    reviewSha256: hash,
    claimCount: z.number().int().positive(),
    receivedAt: z.string().datetime(),
    status: z.literal("RECEIVED_REVIEWED_CLAIMS"),
    processing: sourceDocumentProcessingReadinessSchema,
    sourceAcceptance: z.literal("EXTRACTION_ONLY"),
    fiscalApproval: z.literal("NOT_GRANTED"),
    catalogBinding: z.object({ status: z.literal("PENDING_EXPLICIT_BINDING") }).strict(),
    calculation: z
        .object({
        status: z.literal("UNCALCULATED"),
        reason: z.enum([
            "CALCULATION_NOT_REQUESTED",
            "CALCULATION_REFERENCE_NOT_VERIFIED",
            "CALCULATION_SOURCE_LINK_REQUIRED",
        ]),
        verification: sourceCalculationVerificationSchema,
        reference: sourceCalculationReferenceSchema.optional(),
    })
        .strict(),
    classificationReview: sourceClassificationReviewReferenceSchema.optional(),
    supplierDeclaration: sourceSupplierDeclarationSchema.optional(),
})
    .strict();
export const sourceDocumentIntakeStatusSchema = z
    .object({
    intakeId: z.string().uuid(),
    status: z.enum(["DRAFT", "PARTIAL", "RECEIVED_REVIEWED_CLAIMS"]),
    manifest: sourceDocumentManifestSchema,
    receivedClaims: z.number().int().nonnegative(),
    receivedChunks: z.number().int().nonnegative(),
    receipt: sourceDocumentIntakeReceiptSchema.nullable(),
})
    .strict();
export const sourceDocumentBindingCreateSchema = z
    .object({
    machineSubject: z.string().regex(/^\d{10,32}$/),
    machineEmail: z
        .string()
        .email()
        .regex(/^[a-z0-9._-]+@[a-z0-9-]+\.iam\.gserviceaccount\.com$/),
    workflowId: opaque,
    sellerId: opaque,
    supplierId: z.string().uuid(),
    allowedIdentifierNamespaces: z
        .array(sourceIdentifierNamespaceSchema)
        .min(1)
        .max(3)
        .refine((v) => new Set(v).size === v.length, "Namespace permissions must be distinct"),
    authorizationReference: sourceClassificationReviewReferenceSchema,
    reason: z.string().trim().min(10).max(1000),
})
    .strict();
export const sourceDocumentBindingSchema = sourceDocumentBindingCreateSchema.extend({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    environment: z.literal("dev"),
    createdAt: z.string().datetime(),
    createdBy: z.string().uuid(),
    revokedAt: z.string().datetime().nullable(),
    revokedReason: z.string().nullable(),
});
export const sourceDocumentBindingRevokeSchema = z
    .object({ reason: z.string().trim().min(10).max(1000) })
    .strict();
/** Provider-neutral exact canonical JSON; array order and string bytes are retained. */
export function canonicalSourceDocumentJson(value) {
    const write = (v) => {
        if (Array.isArray(v))
            return `[${v.map((item) => (item === undefined ? "null" : write(item))).join(",")}]`;
        if (v && typeof v === "object")
            return `{${Object.entries(v)
                .filter(([, item]) => item !== undefined)
                .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
                .map(([key, item]) => `${JSON.stringify(key)}:${write(item)}`)
                .join(",")}}`;
        const result = JSON.stringify(v);
        if (result === undefined)
            throw new TypeError("A canonical source value must be JSON");
        return result;
    };
    return write(value);
}
export function canonicalSourceClaimLine(claim) {
    return `${canonicalSourceDocumentJson(claim)}\n`;
}
//# sourceMappingURL=source-document-intake.js.map